import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Diamond, Smartphone, ShieldCheck, Copy, Check, LogOut } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Setup2FA() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [totp, setTotp] = useState({ secret: "", qr: "" });
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "owner") { navigate("/", { replace: true }); return; }
    // Guard: don't yank the user away after successful activation — they must see and save recovery codes
    if (recovery.length > 0) return;
    if (user.totp_enabled) { navigate("/", { replace: true }); return; }
    api.post("/auth/totp/init").then((r) => setTotp({ secret: r.data.totp_secret, qr: r.data.qr_data_url })).catch((e) => toast.error(e?.response?.data?.detail || "Başlatılamadı"));
  }, [user, navigate, recovery.length]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/confirm", { code: code.trim() });
      setRecovery(data.recovery_codes || []);
      toast.success("2FA aktifleştirildi — kurtarma kodlarınızı saklayın");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod hatalı");
    } finally { setBusy(false); }
  };

  const copyRecovery = () => {
    navigator.clipboard.writeText(recovery.join("\n")).then(() => { setCopied(true); toast.success("Panoya kopyalandı"); }).catch(() => toast.error("Kopyalanamadı"));
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center p-6">
      <div className="w-full max-w-lg card-lux p-8" data-testid="setup2fa-root">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-2">
            <Diamond className="w-5 h-5" style={{ color: "#c5a059" }} />
            <span className="font-mono-code text-xs tracking-[0.35em] text-stone-500">RIVACO · GÜVENLİK</span>
          </div>
          <h1 className="font-serif-display text-3xl text-stone-900">İki Faktörlü Doğrulama Kurulumu</h1>
          <p className="text-xs text-stone-500 mt-2">Owner hesabınız için Authenticator uygulaması kurmadan devam edemezsiniz.</p>
        </div>

        {recovery.length === 0 && (
          <>
            <div className="flex items-center gap-2 mb-3"><Smartphone className="w-4 h-4 text-amber-700" /><h2 className="font-medium">Adım 1 — QR kodu tarayın</h2></div>
            {totp.qr ? <img data-testid="setup2fa-qr" src={totp.qr} alt="TOTP QR" className="w-48 h-48 mx-auto mb-3 border border-stone-200 rounded-lg" /> : <div className="text-stone-500 text-center py-8">Yükleniyor…</div>}
            <div className="text-[11px] text-center text-stone-500 mb-3 font-mono-code break-all">Secret: {totp.secret}</div>
            <div className="flex items-center gap-2 mb-3"><Smartphone className="w-4 h-4 text-amber-700" /><h2 className="font-medium">Adım 2 — 6 haneli kodu girin</h2></div>
            <input data-testid="setup2fa-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" className="w-full px-4 py-3 rounded-lg border border-stone-200 tracking-[0.4em] text-center text-lg outline-none focus:border-amber-500" />
            <button data-testid="setup2fa-confirm" onClick={confirm} disabled={busy || code.length !== 6} className="w-full mt-4 py-3 rounded-lg gold-gradient text-white disabled:opacity-60">Onayla ve Aktifleştir</button>
            <button onClick={doLogout} className="w-full mt-3 py-2 rounded-lg border border-stone-200 text-xs text-stone-500 hover:bg-stone-50 inline-flex items-center justify-center gap-2"><LogOut className="w-3 h-3" /> Çıkış Yap</button>
          </>
        )}

        {recovery.length > 0 && (
          <div data-testid="setup2fa-recovery">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-emerald-700" /><h2 className="font-medium">Kurtarma Kodlarınız</h2></div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 mb-3">
              Aşağıdaki 10 kurtarma kodunu güvenli bir yerde saklayın. Her kod yalnızca <b>bir kez</b> kullanılabilir. Telefonunuzu kaybederseniz bu kodlarla giriş yapabilirsiniz.
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3 font-mono-code text-sm">
              {recovery.map((c, i) => (<div key={i} className="p-2 border border-stone-200 rounded bg-stone-50 text-center">{c}</div>))}
            </div>
            <button onClick={copyRecovery} data-testid="setup2fa-copy-recovery" className="w-full py-2 rounded-lg border border-stone-200 text-sm inline-flex items-center justify-center gap-2 hover:bg-stone-50">
              {copied ? <><Check className="w-4 h-4" /> Kopyalandı</> : <><Copy className="w-4 h-4" /> Kodları Kopyala</>}
            </button>
            <button data-testid="setup2fa-continue" onClick={async () => { await refreshUser(); navigate("/", { replace: true }); }} className="w-full mt-3 py-3 rounded-lg gold-gradient text-white">Panele Devam Et</button>
          </div>
        )}
      </div>
    </div>
  );
}
